import { SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TextInput,
  PhoneNumberInput,
  Form,
  SelectInput,
  NewTimezoneSelect,
  Typography,
  PLATFORM_THEME_COLORS,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';

import { MainContainer, FormWrapper } from './general-information-form.styles';

const GeneralInformationForm = ({
  editCompany,
  isSubCompany,
  datevNumbers,
  setCopmanyDatevNumber,
  canAddDatevNumber,
  onTimeZoneSelect,
  timezone,
}: {
  editCompany?: any;
  isSubCompany?: boolean;
  datevNumbers: any;
  setCopmanyDatevNumber: SetStateAction<any>;
  canAddDatevNumber: boolean;
  timezone: string;
  onTimeZoneSelect: (value: string, options: any) => void;
}) => {
  const [parentCompanies, setParentCompanies] = useState<any[]>([]);

  const { t } = useTranslation(['common', 'settings']);

  const { companies, loading } = useGetAllPlatformCompanies();

  useEffect(() => {
    if (!loading) {
      setParentCompanies(companies);
    }
  }, [loading, companies]);

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainer>
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('settings:company.labels.generalInformation')}
      </Typography>
      <FormWrapper>
        {isSubCompany && (
          <Form.Item
            name="parentCompanyId"
            label={t('settings:company.labels.parentCompany')}
            rules={[
              { required: true, message: t('settings:company.exceptions.requiredParentCompany') },
            ]}
          >
            <SelectInput placeholder="TECHlogo">
              {parentCompanies
                .filter(company => company._id !== editCompany._id)
                .map(company => (
                  <SelectInput.Option key={company._id} value={company._id}>
                    {company.name}
                  </SelectInput.Option>
                ))}
            </SelectInput>
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label={t('settings:company.labels.companyName')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredCompanyName') },
          ]}
        >
          <TextInput
            placeholder={t('settings:company.placeholders.companyName')}
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={t('settings:company.labels.companyEmailAddress')}
          rules={[{ type: 'email', message: t('common:validation.error.incorrectFormat') }]}
        >
          <TextInput maxLength={50} placeholder="techlogo@gmail.com" />
        </Form.Item>

        <Form.Item
          name="ceo"
          label={t('settings:company.labels.ceo')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredCeo') },
            { max: 50, message: t('settings:company.exceptions.requiredCeo') },
          ]}
        >
          <TextInput placeholder="Max Mustermann" maxLength={50} />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label={t('settings:company.labels.companyPhoneNumber')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredPhoneNumber') },
          ]}
        >
          <PhoneNumberInput placeholder="+49 123 123 12345" />
        </Form.Item>

        {canAddDatevNumber && (
          <Form.Item
            name="datevNumberId"
            label={t('settings:company.labels.datevNumber')}
            rules={[
              {
                required: canAddDatevNumber,
                message: t('settings:company.exceptions.requiredDATEVNumber'),
              },
            ]}
          >
            <SelectInput placeholder="№346347" onSelect={setCopmanyDatevNumber}>
              {editCompany.datevNumber && (
                <SelectInput.Option value={editCompany.datevNumber._id}>
                  {editCompany.datevNumber.datevNumber}
                </SelectInput.Option>
              )}

              {datevNumbers.map((datevNumber: any) => (
                <SelectInput.Option value={datevNumber._id} key={datevNumber._id}>
                  {datevNumber.datevNumber}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
        )}

        <Form.Item
          name="timezone"
          label={t('settings:company.labels.timezone')}
          rules={[{ required: true, message: t('settings:company.exceptions.requiredTimezone') }]}
        >
          <NewTimezoneSelect
            placeholder="GMT +02:00"
            value={timezone}
            onSelect={onTimeZoneSelect}
          />
        </Form.Item>

        <Form.Item
          name="mainStreet"
          label={t('settings:company.labels.companyMainStreet')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredMainStreet') },
            { max: 33, message: t('settings:company.exceptions.requiredMainStreet') },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                return /[-a-zA-Z0-9.")]$/.test(value)
                  ? Promise.resolve()
                  : Promise.reject(t('settings:company.exceptions.invalidMainStreet'));
              },
            },
          ]}
        >
          <TextInput placeholder="Am Hoppenhof" maxLength={33} />
        </Form.Item>

        <Form.Item
          name="mainHouseNumber"
          label={t('settings:company.labels.companyMainHouseNumber')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredMainHouseNumber') },
            { max: 9, message: t('settings:company.exceptions.requiredMainHouseNumber') },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                return /\d/.test(value)
                  ? Promise.resolve()
                  : Promise.reject(t('settings:company.exceptions.invalidMainHouseNumber'));
              },
            },
          ]}
        >
          <TextInput placeholder="26A" maxLength={9} />
        </Form.Item>

        <Form.Item
          name="mainZip"
          label={t('settings:company.labels.companyMainZip')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredMainZip') },
            { max: 5, message: t('settings:company.exceptions.requiredMainZip') },
          ]}
        >
          <TextInput placeholder="12345" maxLength={5} />
        </Form.Item>

        <Form.Item
          name="mainCity"
          label={t('settings:company.labels.companyMainCity')}
          rules={[
            { required: true, message: t('settings:company.exceptions.requiredMainCity') },
            { max: 34, message: t('settings:company.exceptions.requiredMainCity') },
          ]}
        >
          <TextInput placeholder="Paderborn" maxLength={34} />
        </Form.Item>
      </FormWrapper>
    </MainContainer>
  );
};

export default GeneralInformationForm;
